import React, { useMemo, useState } from "react";
import styles from "./AppSidebar.module.css";
import { useUIStore } from "../../store/uiStore";
import ConversationHistory from "../chat/ConversationHistory";
import { trackEvent } from "../../services/analyticsService";
import { useChatStore } from "../../store/chatStore";
import { useMindspaceStore } from "../../store/mindspaceStore";
import { HomeIcon, DocumentsIcon, PromptsIcon, BrainIcon, PlusIcon, RobotIcon } from "../common/Icons";
import useMediaQuery from "../../hooks/useMediaQuery";

const AppSidebar: React.FC = () => {
  const { route, navigate } = useUIStore();
  const startNewChatSession = useChatStore((s) => s.startNewChatSession);

  const handleNav = (nextRoute: Parameters<typeof navigate>[0]) => {
    navigate(nextRoute);
    trackEvent("fullpage_nav_clicked", { route: nextRoute });
  };

  // Mindspace mini and modal (placeholder)
  const { defaultMindspace } = useMindspaceStore();
  const currentMindspaceName = useMemo(
    () => defaultMindspace?.name || "Default Mindspace",
    [defaultMindspace?.name],
  );

  const [isMindspaceModalOpen, setIsMindspaceModalOpen] = useState(false);

  const openMindspaceModal = () => {
    setIsMindspaceModalOpen(true);
    trackEvent("mindspace_modal_opened", { mode: "fullpage" });
  };

  const closeMindspaceModal = () => setIsMindspaceModalOpen(false);

  return (
    <div className={styles.sidebarRoot}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <RobotIcon className={styles.navIcon} title="Smartie AI" />
          <span>Smartie AI (beta)</span>
        </div>
      </header>

      <ul className={styles.navList} role="list">
        <li>
          <button
            className={`${styles.navItem} ${styles.primary}`}
            onClick={() => {
              startNewChatSession();
              const sid = useChatStore.getState().sessionId;
              navigate("chat", { sessionId: sid });
              trackEvent("fullpage_nav_clicked", { route: "chat", source: "new_chat_nav" });
            }}
          >
            <PlusIcon className={styles.navIcon} title="Chat nou" />
            Chat nou
          </button>
        </li>
        <li>
          <button
            className={styles.navItem}
            aria-current={route === "welcome" ? "page" : undefined}
            onClick={() => handleNav("welcome")}
          >
            <HomeIcon className={styles.navIcon} title="Bun venit" />
            Bun venit
          </button>
        </li>
        <li>
          <button
            className={styles.navItem}
            aria-current={route === "documents" ? "page" : undefined}
            onClick={() => handleNav("documents")}
          >
            <DocumentsIcon className={styles.navIcon} title="Documente" />
            Documente
          </button>
        </li>
        <li>
          <button
            className={styles.navItem}
            aria-current={route === "prompts" ? "page" : undefined}
            onClick={() => handleNav("prompts")}
          >
            <PromptsIcon className={styles.navIcon} title="Prompturi" />
            Prompturi
          </button>
        </li>
        <li>
          <button
            className={styles.navItem}
            aria-current={route === "mindspace" ? "page" : undefined}
            onClick={() => handleNav("mindspace")}
          >
            <BrainIcon className={styles.navIcon} title="Informații Mindspace" />
            Informații Mindspace
          </button>
        </li>
      </ul>

      <div className={styles.mindspaceArea}>
        {/** Show current mindspace only when it truly exists (authenticated users). */}
        {defaultMindspace && (
          <div className={styles.mindspacePlaceholder} aria-live="polite">
            <span>Curent: {currentMindspaceName}</span>
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <button
            className={styles.navItem}
            onClick={openMindspaceModal}
            aria-label="Deschide lista de mindspace-uri"
          >
            Mindspace-uri
          </button>
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.historyWrapper}>
        {/* On phone sizes, show history inline within the hamburger sidebar.
            On tablet/desktop, use default behavior. */}
        <ConversationHistory overlayMode={useMediaQuery("(max-width: 767px)") ? "inlineInSidebar" : null} />
      </div>

      <footer className={styles.footer}>
        <span>© SMARTERS</span>
      </footer>

      {isMindspaceModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="mindspace-modal-title"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={closeMindspaceModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              width: "min(520px, 92vw)",
              maxHeight: "80vh",
              overflow: "auto",
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="mindspace-modal-title" style={{ marginTop: 0 }}>
              Mindspace-uri (în curând)
            </h3>
            <p style={{ color: "#374151", marginBottom: 12 }}>
              În această fereastră vei putea vedea lista completă de mindspace-uri
              și vei putea crea unele noi. Momentan, funcționalitatea este în
              lucru.
            </p>
            <ul style={{ paddingLeft: 18 }}>
              <li>{currentMindspaceName} (implicit)</li>
            </ul>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className={styles.navItem} disabled title="În curând">
                Creează nou
              </button>
              <button className={styles.navItem} onClick={closeMindspaceModal}>
                Închide
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppSidebar;


